import { RigidBody } from '@react-three/rapier';
import { Player, type PlayerHandle } from './Player';
import { Terrain, getHeight } from './Terrain';
import { Interactable } from './Interactable';
import { House } from './House';
import { Forest } from './Forest';
import { GrassField } from './Grass';
import { Rocks } from './Rocks';
import { InteractiveBoard } from './InteractiveBoard';
import { River } from './River';
import { Bridge } from './Bridge';
import { MapLimits } from './MapLimits';
import { RemotePlayer } from './RemotePlayer';
import { Fireplace } from './Fireplace';
import { ProjectileManager } from './ProjectileManager';
import * as Colyseus from 'colyseus.js';
import { NpcRenderer } from './characters/NpcRenderer';
import type { MyRoomState, Player as PlayerSchema } from '../../schema/GameState';
import { useOcclusionUpdater } from '../../utils/Transparency';
import { FishingManager } from './ecosystem/FishingManager';
import { useRef, useEffect } from 'react';
import * as THREE from 'three';
import type { ThreeEvent } from '@react-three/fiber';
import { isMobile } from '../../utils/isMobile';
import { useStore } from '../../store/useStore';

interface WorldProps {
    room: Colyseus.Room<MyRoomState> | null;
    remotePlayers: Map<string, PlayerSchema>;
}

export const World = ({ room, remotePlayers }: WorldProps) => {
    // Sync room with store
    const setRoom = useStore((state) => state.setRoom);
    useEffect(() => {
        setRoom(room);
    }, [room, setRoom]);

    // Occlusion Uniform Updater
    const playerRef = useRef<PlayerHandle>(null);
    const occlusionRef = useRef<THREE.Group>(null);

    // Check if useOcclusionUpdater can handle null
    // Assuming useOcclusionUpdater expects a ref object that might be null initially
    useOcclusionUpdater(occlusionRef as any);

    const handlePlayerRef = (handle: PlayerHandle | null) => {
        playerRef.current = handle;
        if (handle && handle.mesh) {
            (occlusionRef as any).current = handle.mesh;
        }
    };

    const handleTerrainClick = (e: ThreeEvent<MouseEvent>) => {
        // Only allow touch-to-move on Mobile!
        if (!isMobile()) return;

        if (playerRef.current) {
            e.stopPropagation();
            playerRef.current.moveTo(e.point);
        }
    };

    const sessionPlayer = room?.state.players.get(room.sessionId);
    const initialPos: [number, number, number] = sessionPlayer
        ? [sessionPlayer.x, sessionPlayer.y, sessionPlayer.z]
        : [0, 5, 10];

    return (
        <group>
            <Player
                ref={handlePlayerRef}
                initialPos={initialPos}
                room={room}
                onMove={(pos: any, rx: number) => {
                    if (room) {
                        room.send("move", { x: pos.x, y: pos.y, z: pos.z, rx });
                    }
                }} />

            {/* NPCs */}
            {/* NPCs */}
            <NpcRenderer room={room} />

            {Array.from(remotePlayers.entries()).map(([key, p]) => (
                // Don't render ourselves twice
                key !== room?.sessionId && p.currentScene === 'world' && <RemotePlayer key={key} player={p} />
            ))}

            <Terrain onClick={handleTerrainClick} />

            <River />
            <Bridge />
            <MapLimits />

            {/* Trees */}
            <Forest />

            {/* Grass (Shadowless on mobile) */}
            <GrassField lowPerf={isMobile()} />
            <Rocks />

            {/* Projectiles & Hits (Isolated for performance) */}
            <ProjectileManager />

            {/* Interactive Boards */}
            <InteractiveBoard
                position={[6, getHeight(6, 8), 8]}
                rotation={[0, -Math.PI / 8, 0]}
                title="Welcome!"
                description="Explore the world, find the 3 houses, and watch out for enemies in the arena!"
            />
            <InteractiveBoard
                position={[-10, getHeight(-10, 12), 12]}
                rotation={[0, Math.PI / 8, 0]}
                title="Controls"
                description="WASD to Move, Shift to Run, Space to Jump. Click to Interact/Attack."
            />

            {/* Houses */}
            <House position={[-140, getHeight(-140, -10), -10]} contentId="about" label="About" color="#4a90e2" />
            <House position={[60, getHeight(60, -140), -140]} contentId="personal_projects" label="Personal Projects" color="#e24a4a" />
            <House position={[160, getHeight(160, 10), 10]} contentId="apps" label="Apps" color="#f5a623" />
            <House position={[-120, getHeight(-120, 90), 90]} contentId="games" label="Games" color="#6a5acd" />
            <House position={[-130, getHeight(-130, -140), -140]} contentId="simulations" label="Simulations" color="#3cb371" />

            {/* Interactables (Server Authority) */}
            {room && room.state.items && Array.from(room.state.items.entries()).map(([id, item]) => (
                <Interactable
                    key={id}
                    id={id}
                    position={[item.x, item.y, item.z]}
                    name={item.name}
                    color={item.name.includes("Health") ? "red" : item.name.includes("Mana") ? "blue" : "gold"}
                />
            ))}

            {/* Combat Arena Portal */}
            <Interactable
                position={[-130, getHeight(-130, -140), -140]}
                name="Enter Arena"
                color="purple"
                onInteract={() => useStore.getState().setCurrentScene('arena')}
            />

            {/* Merchant Shop */}
            <Interactable
                position={[-10, getHeight(-10, -80), -80]}
                name="Merchant"
                color="green"
                onInteract={() => useStore.getState().toggleShop()}
            />

            {/* Centerpiece Fireplace */}
            <Fireplace position={[0, getHeight(0, 0), 0]} />

            {/* Fishing System */}
            <FishingManager />
        </group>
    );
};
